/**
 * FarmersMarkets — public listing of physical farmers markets, with a
 * user-submission modal. Anyone can submit; admin approval required
 * before it goes public (test admins see their own submissions
 * auto-approved).
 *
 * Route: /food-pulse/markets
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  MapPin, Calendar, Plus, X, Sun, Loader2, Sprout, Award, Clock,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;
const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABEL = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

function MarketCard({ m }) {
  const days = (m.schedule || []).map((s) => DAY_LABEL[s.day] || s.day);
  return (
    <div className="rounded-2xl border overflow-hidden bg-white hover:-translate-y-0.5 hover:shadow-lg transition-all"
         style={{ borderColor: "#E9D8B4" }}
         data-testid={`market-card-${m.slug}`}>
      <div className="aspect-[16/9] overflow-hidden" style={{ background: `linear-gradient(135deg,#F6C87A,${CREAM})` }}>
        {m.hero_image_url && <img src={m.hero_image_url} alt={m.name} className="w-full h-full object-cover" />}
        {!m.hero_image_url && (
          <div className="w-full h-full flex items-center justify-center opacity-30">
            <Sprout className="w-16 h-16" style={{ color: GREEN }} />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-display text-xl leading-tight" style={{ color: GREEN }}>{m.name}</h3>
          {m.is_in_season && <span className="rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest" style={{ background: "#F0F5F1", color: GREEN }}><Sun className="w-3 h-3 inline" /> in-season</span>}
        </div>
        {(m.city || m.state) && (
          <div className="text-sm opacity-70 flex items-center gap-1 mb-2">
            <MapPin className="w-3.5 h-3.5" />{m.city}{m.state ? `, ${m.state}` : ""}
          </div>
        )}
        {days.length > 0 && (
          <div className="text-xs mb-2 flex items-center gap-1"><Calendar className="w-3 h-3" /> {days.join(" · ")}</div>
        )}
        {m.description && <p className="text-sm opacity-80 line-clamp-2 mb-3">{m.description}</p>}
        {(m.tags || []).slice(0, 3).map((t) => (
          <span key={t} className="mr-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest" style={{ background: "#FFF7E4", color: "#8B5E10" }}>#{t}</span>
        ))}
      </div>
    </div>
  );
}

function SubmitModal({ open, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", city: "", state: "", country: "United States", description: "", address: "", tags: "" });
  const [days, setDays] = useState({ sat: { start_time: "09:00", end_time: "13:00" } });
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const submit = async () => {
    if (form.name.trim().length < 3) return alert("Name is required (min 3 chars)");
    setBusy(true);
    try {
      const payload = {
        ...form,
        schedule: Object.entries(days).map(([day, t]) => ({ day, ...t })),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      const r = await http.post(`/farmers-markets`, payload);
      onDone(r.data);
    } catch (e) { alert(e?.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-16 px-4" onClick={onClose} data-testid="market-submit-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center gap-3" style={{ borderColor: "#E9D8B4" }}>
          <Sprout className="w-5 h-5" style={{ color: GREEN }} />
          <div className="font-display text-xl">Submit a farmers market</div>
          <button onClick={onClose} className="ml-auto opacity-60"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto grid gap-3">
          <input placeholder="Market name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="market-name" />
          <textarea rows={2} placeholder="Short description (what's special)" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
          <div className="grid grid-cols-3 gap-2">
            <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                   className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="market-city" />
            <input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                   className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
            <input placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                   className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
          </div>
          <input placeholder="Street address" value={form.address}
                 onChange={(e) => setForm({ ...form, address: e.target.value })}
                 className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest opacity-70 mb-1">Days &amp; hours</div>
            <div className="flex flex-wrap gap-2">
              {DAYS.map((d) => {
                const on = !!days[d];
                return (
                  <button key={d} type="button"
                          onClick={() => setDays((prev) => {
                            const next = { ...prev };
                            if (next[d]) delete next[d];
                            else next[d] = { start_time: "09:00", end_time: "13:00" };
                            return next;
                          })}
                          className={`rounded-full px-3 py-1 text-xs font-semibold border-2`}
                          style={{ borderColor: on ? GREEN : "#E9D8B4",
                                   background: on ? GREEN : "transparent",
                                   color: on ? "#fff" : GREEN }}
                          data-testid={`market-day-${d}`}>
                    {DAY_LABEL[d]}
                  </button>
                );
              })}
            </div>
            <div className="grid gap-2 mt-2">
              {Object.entries(days).map(([d, t]) => (
                <div key={d} className="flex items-center gap-2 text-xs">
                  <span className="w-10">{DAY_LABEL[d]}</span>
                  <input type="time" value={t.start_time} onChange={(e) => setDays({ ...days, [d]: { ...t, start_time: e.target.value } })}
                         className="rounded border px-2 py-1" style={{ borderColor: "#E9D8B4" }} />
                  <span>→</span>
                  <input type="time" value={t.end_time} onChange={(e) => setDays({ ...days, [d]: { ...t, end_time: e.target.value } })}
                         className="rounded border px-2 py-1" style={{ borderColor: "#E9D8B4" }} />
                </div>
              ))}
            </div>
          </div>
          <input placeholder="Tags (comma separated): organic, artisan, seafood"
                 value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                 className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
          <div className="rounded-xl p-3 text-sm" style={{ background: "#FFF7E4" }}>
            <Award className="w-4 h-4 inline mr-1" style={{ color: AMBER }} />
            Submitters earn <strong>+15 points</strong>. Admins review before it goes public.
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: "#E9D8B4" }}>
          <button onClick={onClose} className="rounded-full px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy}
                  className="rounded-full px-6 py-2 text-sm font-semibold text-white flex items-center gap-2"
                  style={{ background: GREEN }}
                  data-testid="market-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Submit for review
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FarmersMarkets() {
  const { user } = useAuth();
  const [markets, setMarkets] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inSeasonOnly, setInSeasonOnly] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/farmers-markets`, { params: { in_season: inSeasonOnly, limit: 60 } }),
      axios.get(`${API}/api/farms/featured`, { params: { limit: 8 } }).catch(() => ({ data: { items: [] } })),
    ]).then(([m, f]) => {
      setMarkets(m.data.items || []);
      setFeatured(f.data.items || []);
    }).finally(() => setLoading(false));
  }, [inSeasonOnly]);

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: GREEN }} data-testid="farmers-markets-page">
      <SeoHead section="food-pulse-markets" title="Farmers Markets — Food Pulse" description="Physical farmers markets near you, with schedule and participating farms." />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: GREEN, color: "#fff" }}>
        <div className="absolute inset-0"
             style={{ backgroundImage: `linear-gradient(115deg, ${GREEN} 0%, rgba(15,61,46,0.75) 45%, rgba(15,61,46,0.25) 100%), url('/illustrations/food-categories/hero/farmers_market.webp')`, backgroundSize: "cover", backgroundPosition: "center" }}
             aria-hidden />
        <div className="relative max-w-5xl mx-auto px-4 py-14 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border-2 px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.28em] mb-4"
               style={{ borderColor: AMBER, color: "#F6C87A" }}>
            <Sprout className="w-3.5 h-3.5" /> Farmers markets
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight">
            Real markets. <span style={{ color: AMBER }}>Real farmers.</span>
          </h1>
          <p className="mt-4 max-w-2xl opacity-90 text-sm sm:text-base">
            Village and city farmers markets across the network. Know your growers, meet your fishermen, buy at the source.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Featured farms carousel */}
        {featured.length > 0 && (
          <div className="mb-10">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display text-2xl">★ Featured small farms</h2>
              <div className="text-xs opacity-70">Support the growers who need it most</div>
            </div>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {featured.map((f) => (
                <Link key={f.slug} to={`/food-pulse/businesses/${f.slug}`}
                      className="block rounded-xl border overflow-hidden bg-white hover:-translate-y-0.5 hover:shadow-lg transition-all"
                      style={{ borderColor: "#E9D8B4" }}
                      data-testid={`featured-farm-${f.slug}`}>
                  <div className="aspect-square overflow-hidden" style={{ background: `linear-gradient(135deg,#F6C87A,${CREAM})` }}>
                    {f.hero_image && <img src={f.hero_image} alt={f.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-2">
                    <div className="font-semibold text-sm truncate">{f.name}</div>
                    <div className="text-[10px] opacity-70">{f.city}, {f.state_code || f.state}</div>
                    {(f.farm?.badges || []).slice(0, 2).map((b) => (
                      <span key={b} className="mt-1 mr-1 inline-block rounded-full px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest" style={{ background: "#F0F5F1", color: GREEN }}>{b.replace(/_/g, " ")}</span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Filters + submit */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: AMBER }}>— Markets</div>
            <h2 className="font-display text-3xl">{markets.length} market{markets.length === 1 ? "" : "s"}</h2>
          </div>
          <div className="flex gap-2 items-center">
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={inSeasonOnly} onChange={(e) => setInSeasonOnly(e.target.checked)} data-testid="market-in-season-toggle" />
              In-season only
            </label>
            {user && (
              <button onClick={() => setModalOpen(true)}
                      className="rounded-full px-4 py-2 text-white text-sm font-semibold flex items-center gap-2"
                      style={{ background: GREEN }}
                      data-testid="market-add-cta">
                <Plus className="w-4 h-4" /> Add a market
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 opacity-60">Loading markets…</div>
        ) : markets.length === 0 ? (
          <div className="rounded-3xl border border-dashed p-14 text-center" style={{ borderColor: AMBER, background: "#FFF7E4" }}>
            <Sprout className="w-14 h-14 mx-auto opacity-40 mb-3" />
            <div className="font-display text-2xl mb-2">No markets listed yet</div>
            <div className="opacity-70 text-sm max-w-md mx-auto mb-4">
              Know a farmers market in your village or city? Add it in seconds — you'll earn Food Scout points and help every shopper find fresh, local food.
            </div>
            {user && (
              <button onClick={() => setModalOpen(true)} className="rounded-full px-6 py-3 text-white font-semibold" style={{ background: GREEN }} data-testid="market-empty-add">
                <Plus className="w-4 h-4 inline mr-1" /> Add the first market
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {markets.map((m) => <MarketCard key={m.slug} m={m} />)}
          </div>
        )}
      </div>

      <SubmitModal open={modalOpen} onClose={() => setModalOpen(false)}
                   onDone={(d) => { setModalOpen(false); setMarkets((prev) => [d, ...prev]); alert(d.approved ? "Live!" : "Submitted — pending admin review. +15 points added."); }} />
    </div>
  );
}
